import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trash, PlusCircle } from 'phosphor-react';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker from './AppDatePicker';
import RadioInputs from '../CreditApplicationComponents/RadioInput';
import { UploadFileInputs } from '../CreditApplicationComponents/UploadFileInput';
import { AppButton } from '../../../general/AppButton/AppButton';
import { COLORS } from '../../../../utils/colors';
import { DynamicInvitedSignaturesFields } from '@dill/dill-shared';
import CheckboxList from './CheckboxList';
import StateDropDown from './StateDropDown';

const FIELD_TYPE = [
  'TEXT',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'TEXT_AREA',
  'FILE',
  'OPTION_FIELD',
  'STATE_DROPDOWN',
  'CHECKBOX_LIST',
];
const COLUMN_TYPES = ['ONE_COLUMN', 'TWO_COLUMNS', 'THREE_COLUMNS'];

interface SignatureDynamicFieldsProps {
  onSubmit: (data: { fields: DynamicInvitedSignaturesFields[] }) => void;
  errorSignatureFields: { [key: string]: { name?: string; id?: string } };
  valueData: DynamicInvitedSignaturesFields[];
  selectedSignatureFields?: DynamicInvitedSignaturesFields[] | null;
  filteredStardardFieldIds: string[];
}

const SignatureDynamicFields: React.FC<SignatureDynamicFieldsProps> = ({
  onSubmit,
  errorSignatureFields,
  selectedSignatureFields,
  filteredStardardFieldIds,
}) => {
  const [signatureFields, setSignatureFields] = useState<DynamicInvitedSignaturesFields[]>([
    {
      name: '',
      id: '',
      fromField: 'TEXT',
      isRequired: false,
      prefix: '',
      suffix: '',
      index: 0,
    },
  ]);
  const [fieldColumn, setFieldColumn] = useState<
    {
      column: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
      index: number;
    }[]
  >([{ column: 'ONE_COLUMN', index: 0 }]);
  const [isIdAutoCompleteOpen, setIsIdAutoCompleteOpen] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsIdAutoCompleteOpen('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsIdAutoCompleteOpen]);

  useEffect(() => {
    if (selectedSignatureFields) {
      const columnType = (length: number): 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS' => {
        if (length === 3) return 'THREE_COLUMNS';
        if (length === 2) return 'TWO_COLUMNS';
        return 'ONE_COLUMN';
      };

      const grouped = Object.values(
        selectedSignatureFields.reduce((acc, item) => {
          acc[item.index] = acc[item.index] || [];
          acc[item.index].push(item);
          return acc;
        }, {} as Record<number, typeof selectedSignatureFields>)
      ).map((group) => ({
        index: group[0].index,
        column: columnType(group.length),
      }));

      setFieldColumn(grouped);
      setSignatureFields(selectedSignatureFields);
    }
  }, [selectedSignatureFields]);

  const updateField = <K extends keyof DynamicInvitedSignaturesFields>(
    globalIndex: number,
    key: K,
    value: DynamicInvitedSignaturesFields[K],
    fieldIndex: number
  ): void => {
    setSignatureFields((prevFields) => {
      const updatedFields = [...prevFields];

      // Track how many fields match the group (by index)
      let matchCount = 0;

      for (let i = 0; i < updatedFields.length; i++) {
        if (updatedFields[i].index === fieldIndex) {
          if (matchCount === globalIndex) {
            updatedFields[i] = {
              ...updatedFields[i],
              [key]: value,
            };
            break;
          }
          matchCount++;
        }
      }

      return updatedFields;
    });
  };

  useEffect(() => {
    if (signatureFields.length > 0) {
      onSubmit({ fields: signatureFields });
    }
  }, [signatureFields]);

  const getColumnsCount = (type: string): number =>
    type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

  const handleColumnTypeChange = (item: { value: string } | null, index: number) => {
    if (!item) return;

    setFieldColumn((prev) => {
      const updatedColumns = prev.map((col) =>
        col.index === index
          ? { ...col, column: item.value as 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS' }
          : col
      );
      return updatedColumns;
    });

    const columnCount = getColumnsCount(item.value);

    setSignatureFields((prev) => {
      const updatedFields = prev.filter((field) => field.index !== index);

      const newFields = Array(columnCount)
        .fill(null)
        .map(() => ({
          name: '',
          id: '',
          fromField: 'TEXT' as const,
          isRequired: false,
          prefix: '',
          suffix: '',
          index: index,
        }));
      return updatedFields.concat(newFields);
    });
  };

  const addNewField = () => {
    const maxIndex = Math.max(...signatureFields.map((f) => f.index), -1);
    setSignatureFields((prev) => [
      ...prev,
      {
        name: '',
        id: '',
        fromField: 'TEXT',
        isRequired: false,
        prefix: '',
        suffix: '',
        index: maxIndex + 1,
      },
    ]);
    setFieldColumn((prev) => [...prev, { column: 'ONE_COLUMN', index: maxIndex + 1 }]);
  };

  const removeGroup = (index: number) => {
    setSignatureFields((prev) => prev.filter((_, i) => i !== index));
    setFieldColumn((prev) => prev.filter((col) => col.index !== index));
  };

  const fieldsGroup = useMemo(() => {
    const grouped = signatureFields.reduce((acc, item) => {
      if (!acc[item.index]) acc[item.index] = [];
      acc[item.index].push(item);
      return acc;
    }, {} as Record<number, typeof signatureFields>);

    // Convert to array of arrays
    const groupedArray = Object.values(grouped);
    return groupedArray;
  }, [signatureFields]);

  const filteredIdSuggestionsOptions = (text: string) => {
    return filteredStardardFieldIds.filter((option) =>
      option.toLowerCase().includes(text.trim().toLowerCase())
    );
  };

  const renderFieldInputs = (fields: DynamicInvitedSignaturesFields[]) => {
    return fields.map((field, idx) => {
      const idSuggestedOptions = filteredIdSuggestionsOptions(field.id);
      return (
        <div key={idx} className="w-full rounded GREY_300-BORDER border p-2">
          <div className="w-full mt-2 mb-4">
            <AppInputField
              isRequired
              placeholder="Enter field name"
              label="Field Name"
              value={field.name}
              errorText={errorSignatureFields[`signature-${idx}-${field.index}`]?.name || ''}
              onTextChange={(text) => {
                updateField(idx, 'name', text, field.index);
              }}
            />
          </div>
          <div className="w-full mt-2 mb-4 relative" ref={wrapperRef}>
            <AppInputField
              isRequired
              placeholder="Enter field id"
              label="Field Id"
              value={field.id}
              onFocus={() =>
                setIsIdAutoCompleteOpen(`focus-id-${idx}-${field.index}-${field.name}`)
              }
              onMouseUp={() =>
                setIsIdAutoCompleteOpen(`focus-id-${idx}-${field.index}-${field.name}`)
              }
              errorText={errorSignatureFields[`signature-${idx}-${field.index}`]?.id || ''}
              onBlur={() => {
                if (field.id?.trim() !== field.id) {
                  updateField(idx, 'id', field.id.trim(), field.index);
                }
              }}
              onTextChange={(text) => {
                updateField(idx, 'id', text, field.index);
              }}
            />
            {isIdAutoCompleteOpen === `focus-id-${idx}-${field.index}-${field.name}` &&
              idSuggestedOptions.length > 0 && (
                <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                  <div className="p-2 max-h-60 overflow-y-auto">
                    {idSuggestedOptions.map((option, i) => (
                      <div
                        key={`${option}_${i}-id-option`}
                        onMouseDown={() => {
                          updateField(idx, 'id', option, field.index);
                          setIsIdAutoCompleteOpen('');
                        }}
                        className="p-1 hover:bg-gray-100 cursor-pointer text-xs">
                        {option}
                      </div>
                    ))}
                  </div>
                </div>
              )}
          </div>
          <div className="w-full my-2">
            <AppDropDown
              label="Field Type"
              isRequired
              value={field.fromField}
              items={FIELD_TYPE.map((type) => ({ label: type.replaceAll('_', ' '), value: type }))}
              onSelectChange={(item) => {
                if (item) {
                  updateField(idx, 'fromField', item.value, field.index);
                  updateField(idx, 'isRequired', false, field.index);
                  updateField(idx, 'isHashed', false, field.index);
                }
              }}
            />
          </div>
          {(field.fromField === 'TEXT' ||
            field.fromField === 'NUMBER' ||
            field.fromField === 'CURRENCY') && (
            <>
              <div className="my-2">
                <AppInputField
                  label="Prefix"
                  value={field.prefix}
                  onTextChange={(text) => {
                    updateField(idx, 'prefix', text, field.index);
                  }}
                />
              </div>
              <div className="my-2">
                <AppInputField
                  label="Suffix"
                  value={field.suffix}
                  onTextChange={(text) => {
                    updateField(idx, 'suffix', text, field.index);
                  }}
                />
              </div>
            </>
          )}
          {field.fromField !== 'DOCUMENT_FIELD' &&
            field.fromField !== 'INVITE_USER' &&
            field.fromField !== 'BANK_REFERENCE' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isRequired}
                  onClick={(val: boolean) => {
                    updateField(idx, 'isRequired', val, field.index);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    updateField(idx, 'isRequired', !field.isRequired, field.index);
                  }}>
                  Is Field Required
                </p>
              </div>
            )}
          {field.fromField === 'TEXT' && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isFieldEmail || false}
                onClick={(val: boolean) => {
                  updateField(idx, 'isFieldEmail', val, field.index);
                  if (!val) updateField(idx, 'emailValidationType', undefined, field.index);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  const next = !field.isFieldEmail;
                  updateField(idx, 'isFieldEmail', next, field.index);
                  if (!next) updateField(idx, 'emailValidationType', undefined, field.index);
                }}>
                Is field an email
              </p>
            </div>
          )}
          {field.fromField === 'TEXT' && field.isFieldEmail && (
            <div className="my-2">
              <AppDropDown
                label="Email Validation Type"
                value={field.emailValidationType ?? 'SINGLE'}
                items={[
                  { label: 'Single email', value: 'SINGLE' },
                  { label: 'Multiple emails (comma or semicolon separated)', value: 'MULTIPLE' },
                ]}
                onSelectChange={(item) => {
                  if (item)
                    updateField(
                      idx,
                      'emailValidationType',
                      item.value as 'SINGLE' | 'MULTIPLE',
                      field.index
                    );
                }}
              />
            </div>
          )}
          {field.fromField === 'CURRENCY' && (
            <div className="my-2">
              <AppDropDown
                label="Decimal Places"
                value={String(
                  field.currencyDecimalPlaces !== undefined ? field.currencyDecimalPlaces : 2
                )}
                items={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
                onSelectChange={(item) => {
                  if (item)
                    updateField(
                      idx,
                      'currencyDecimalPlaces',
                      parseInt(item.value, 10),
                      field.index
                    );
                }}
              />
            </div>
          )}
          {field.fromField === 'TEXT' && (
            <div className="my-2">
              <AppDropDown
                label="Validation Type"
                value={field.validationType ?? 'NONE'}
                items={[
                  { label: 'None', value: 'NONE' },
                  { label: 'Tax ID / EIN (XX-XXXXXXX)', value: 'EIN' },
                  { label: 'SSN (XXX-XX-XXXX)', value: 'SSN' },
                  { label: 'Phone Number (XXX-XXX-XXXX)', value: 'PHONE' },
                ]}
                onSelectChange={(item) => {
                  if (item)
                    updateField(
                      idx,
                      'validationType',
                      item.value as 'NONE' | 'EIN' | 'SSN' | 'PHONE',
                      field.index
                    );
                }}
              />
            </div>
          )}
          {(field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') && (
            <div className="my-2">
              <AppInputField
                label="Max Length"
                inputType="number"
                placeholder="No limit"
                value={field.maxLength !== undefined ? field.maxLength : ''}
                onTextChange={(text) => {
                  updateField(
                    idx,
                    'maxLength',
                    text === '' ? undefined : Number(text),
                    field.index
                  );
                }}
              />
              <p className="text-xs ml-1 text-left PRIMARY_300-CLR">
                Maximum number of characters allowed
              </p>
            </div>
          )}
          {(field.fromField === 'TEXT' || field.fromField === 'NUMBER') && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isHashed || false}
                onClick={(val: boolean) => {
                  updateField(idx, 'isHashed', val, field.index);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  updateField(idx, 'isHashed', !field.isHashed, field.index);
                }}>
                Is Field Hashed
              </p>
            </div>
          )}
          {field.fromField === 'OPTION_FIELD' && (
            <div className="my-2 flex items-center">
              <AppRadioButton
                isChecked={field.isRow || false}
                onClick={(val: boolean) => {
                  updateField(idx, 'isRow', val, field.index);
                }}
              />
              <p
                className="text-sm ml-1 cursor-pointer"
                onClick={() => {
                  updateField(idx, 'isRow', !field.isRow, field.index);
                }}>
                Are options in a row or column
              </p>
            </div>
          )}
        </div>
      );
    });
  };

  const renderPreview = (fields: DynamicInvitedSignaturesFields[]) => {
    return fields.map((field, idx) => {
      let fieldComponent = null;

      switch (field.fromField) {
        case 'TEXT':
        case 'NUMBER':
        case 'CURRENCY':
        case 'TEXT_AREA':
          fieldComponent = (
            <AppInputField
              label={field.name}
              inputType={
                field.fromField === 'TEXT_AREA'
                  ? 'textarea'
                  : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                  ? 'number'
                  : 'text'
              }
              value=""
              isRequired={field.isRequired}
              suffix={field.suffix}
              prefix={field.prefix}
              onTextChange={() => {
                // Preview only - no action needed
              }}
            />
          );
          break;

        case 'DATE':
          fieldComponent = (
            <AppDatePicker
              label={field.name}
              selectedDate={new Date()}
              isRequired={field.isRequired}
              onDateSelected={() => {
                // Preview only - no action needed
              }}
            />
          );
          break;

        case 'FILE':
          fieldComponent = (
            <div className="my-1 flex items-center">
              <UploadFileInputs
                isRequired={field.isRequired}
                label={field.name}
                onSelectFile={() => {
                  // Preview only - no action needed
                }}
              />
            </div>
          );
          break;

        case 'OPTION_FIELD':
          fieldComponent = (
            <div className="my-1 flex items-center">
              <RadioInputs
                label={field.name}
                onChange={() => {
                  // Preview only - no action needed
                }}
                id={field.name}
                options={
                  field?.optionList
                    ? field.optionList.split(',').map((option) => option.trim())
                    : []
                }
                isRequired={field.isRequired}
                selectedOption=""
                className={field.isRow ? 'gap-2 flex items-center' : ''}
              />
            </div>
          );
          break;

        case 'STATE_DROPDOWN':
          fieldComponent = (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={''}
              errorText={''}
              onTextChange={() => {}}
            />
          );
          break;

        case 'CHECKBOX_LIST':
          fieldComponent = (
            <CheckboxList
              isRequired={field.isRequired}
              label={field.name}
              options={
                field?.optionList ? field?.optionList.split(',').map((option) => option.trim()) : []
              }
              value={''}
              errorText={''}
              onTextChange={() => {}}
            />
          );
          break;

        default:
          fieldComponent = (
            <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
          );
          break;
      }

      return (
        <div key={idx} className="w-full">
          <div className="px-2 py-2">{fieldComponent}</div>
        </div>
      );
    });
  };

  const getCurrentFields = (
    groupedField: DynamicInvitedSignaturesFields[],
    index: number
  ): DynamicInvitedSignaturesFields[] => {
    const columnType = fieldColumn.find((col) => col.index === index)?.column || 'ONE_COLUMN';

    const columnCount = getColumnsCount(columnType);
    return Array(columnCount)
      .fill(null)
      .map(
        (_, i) =>
          groupedField[i] || {
            name: '',
            id: '',
            fromField: 'TEXT',
            isRequired: false,
            prefix: '',
            suffix: '',
            index: i,
          }
      );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between py-2">
        <h2 className="text-sm font-semibold">Signature Fields</h2>
      </div>
      {/* Body */}
      <div className="px-2 GREY_300-BORDER border-t">
        {fieldsGroup.map((group, index) => (
          <div key={`signature_group_${index}`} className="w-full">
            {fieldsGroup.length > 1 && (
              <div className="flex w-full">
                <div
                  className="w-full cursor-pointer  flex justify-end my-2 text-red-500 items-center"
                  onClick={() => removeGroup(index)}>
                  <div className="text-sm mr-1">Remove</div>
                  <Trash color={COLORS.ERROR_400} size={16} />
                </div>
              </div>
            )}
            <AppDropDown
              label="Number of Columns"
              isRequired
              value={fieldColumn.find((col) => col.index === index)?.column || 'ONE_COLUMN'}
              items={COLUMN_TYPES.map((type) => ({
                label: type.replaceAll('_', ' '),
                value: type,
              }))}
              onSelectChange={(item) => handleColumnTypeChange(item, index)}
            />
            <div className="flex gap-2 mt-2">
              {renderFieldInputs(getCurrentFields(group, index))}
            </div>
          </div>
        ))}
        <div className="w-full flex mb-1 mt-2 justify-end">
          <AppButton
            text={`Add Field Column`}
            onClick={() => addNewField()}
            icon={<PlusCircle color={COLORS.WHITE} size={12} />}
            buttonStyles={{
              height: '30px',
              borderRadius: '5px',
            }}
          />
        </div>
      </div>
      <div className="my-2 p-4">
        <p className="text-sm font-bold mb-2">Preview</p>
        <div className="flex gap-2 mt-2 border border-blue-500 flex-col">
          {fieldsGroup.map((group, index) => (
            <div key={`signature_preview_group_${index}`} className="w-full flex">
              {renderPreview(getCurrentFields(group, index))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SignatureDynamicFields;
